import { useCallback } from 'react';
import { toGameVariant, type ChallengeSettings, type GameVariant } from '@draughts/shared';
import type { OnlineUser } from '../../store/online-store';
import { useAuthStore } from '../../store/auth-store';
import { useLobbyStore } from '../../store/lobby-store';
import { useMultiplayerStore } from '../../store/multiplayer-store';
import { trackTableCreated } from '../../utils/analytics';
import { log } from '../../utils/logger';
import type { AIDifficulty } from '../../utils/ai-difficulty';
import type { AIGameOptions } from '../dashboard/DashboardPlayAIModal';
import CustomTableOverlay from './CustomTableOverlay';

/**
 * Il cablaggio del foglio «Partita personalizzata», in un posto solo.
 *
 * Il foglio è presentazionale: sa comporre le impostazioni e sa a quale delle
 * quattro destinazioni mandarle, ma non sa parlare né con gli store né con le
 * analitiche. Quel pezzo viveva dentro PlayOverlay, ed era l'unico motivo per cui
 * la Sfida diretta non poteva usare lo stesso foglio: Dashboard, landing ed editor
 * avrebbero dovuto ricopiarselo in tre.
 *
 * Tre cose che sembrano dettagli e non lo sono:
 *
 * 1. `toGameVariant`. Il foglio parla la lingua del FILO — 'international', minuscolo,
 *    quella che va al server in `ChallengeSettings` — mentre lo store di gioco vuole
 *    l'enum `GameVariant`, MAIUSCOLO. Senza conversione `VARIANT_CONFIGS['international']`
 *    è `undefined` e `createInitialBoard` esplode dentro `startLocalGame`, PRIMA che il
 *    foglio si chiuda: il bottone sembra semplicemente non rispondere.
 * 2. L'ordine callback → chiusura, che resta quello di prima (vedi il commento in
 *    `CustomTableOverlay.handleSubmit`).
 * 3. `onLaunched`. Le partite che partono davvero devono chiudere anche la schermata
 *    che OSPITA il foglio — il lobby, che nell'istanza globale resta montato sopra la
 *    partita. Il tavolo pubblico no: lì si resta a guardare la propria fila.
 */
interface Props {
  isOpen: boolean;
  /** Chiude il foglio. */
  onClose: () => void;
  defaultVariant: string;
  defaultTimeControl?: string;
  /**
   * Chiude anche ciò che sta sotto, dopo un avvio che porta altrove. Il tavolo
   * pubblico non la chiama: pubblicare non è partire.
   */
  onLaunched?: () => void;
  /**
   * Il tavolo pubblico è una destinazione possibile. L'editor lo spegne: una
   * posizione composta si sfida, non si mette in vetrina.
   */
  allowPublicTable?: boolean;
  onPlayInPerson?: (settings: ChallengeSettings) => void;
  onPlayAI?: (variant: GameVariant, difficulty: AIDifficulty, options: AIGameOptions) => void;
  /** `null` se la sfida e' partita, altrimenti la CHIAVE i18n del motivo del no. */
  onSendChallenge?: (userId: string, settings: ChallengeSettings) => Promise<string | null>;
  onCreateChallengeLink?: (settings: ChallengeSettings) => void;
  onOpenOpeningTables?: (variant: string) => void;
  onSignIn?: () => void;
  onSpectate?: (user: OnlineUser) => void;
  preselectedUser?: OnlineUser;
  defaultOpponent?: 'online' | 'computer' | 'challenge' | 'inperson';
  position?: string;
  lockRated?: boolean;
  rememberSettings?: boolean;
}

export default function CustomTableHost({
  isOpen,
  onClose,
  defaultVariant,
  defaultTimeControl,
  onLaunched,
  allowPublicTable = true,
  onPlayInPerson,
  onPlayAI,
  onSendChallenge,
  onCreateChallengeLink,
  onOpenOpeningTables,
  onSignIn,
  onSpectate,
  preselectedUser,
  defaultOpponent,
  position,
  lockRated,
  rememberSettings,
}: Props) {
  const isGuest = useAuthStore((s) => s.isGuest);
  const busy = useLobbyStore((s) => s.isBusy);
  const createCustomTable = useMultiplayerStore((s) => s.createCustomTable);

  const handleCreate = useCallback(
    async (settings: ChallengeSettings) => {
      trackTableCreated(settings.variant, settings.timeControl);
      return createCustomTable(settings);
    },
    [createCustomTable],
  );

  const handlePlayInPerson = useCallback(
    (settings: ChallengeSettings) => {
      log.info('CustomTableHost', 'in-person pressed', { variant: settings.variant, timeControl: settings.timeControl });
      onPlayInPerson?.(settings);
      onLaunched?.();
    },
    [onLaunched, onPlayInPerson],
  );

  const handlePlayAI = useCallback(
    (variant: string, difficulty: AIDifficulty, options: AIGameOptions) => {
      const variante = toGameVariant(variant);
      log.info('CustomTableHost', 'vs computer pressed', { variant: variante, difficulty });
      onPlayAI?.(variante, difficulty, options);
      onLaunched?.();
    },
    [onLaunched, onPlayAI],
  );

  /*
   * `onLaunched` solo se la sfida e' PARTITA davvero.
   *
   * Chiude la schermata dietro al foglio, e chiuderla su un rifiuto vorrebbe dire
   * portare via anche il foglio — cioe' l'unico posto dove il motivo si puo'
   * leggere, e l'unico dove si puo' rimediare (cambiare avversario, togliere la
   * sfida che si ha in volo). La chiave torna indietro al foglio, che resta
   * aperto e la dice.
   */
  const handleSendChallenge = useCallback(
    async (userId: string, settings: ChallengeSettings): Promise<string | null> => {
      log.info('CustomTableHost', 'direct challenge pressed', { variant: settings.variant, timeControl: settings.timeControl });
      const chiaveErrore = (await onSendChallenge?.(userId, settings)) ?? null;
      if (!chiaveErrore) onLaunched?.();
      return chiaveErrore;
    },
    [onLaunched, onSendChallenge],
  );

  const handleCreateChallengeLink = useCallback(
    (settings: ChallengeSettings) => {
      log.info('CustomTableHost', 'challenge link pressed', { variant: settings.variant, timeControl: settings.timeControl });
      onCreateChallengeLink?.(settings);
      onLaunched?.();
    },
    [onLaunched, onCreateChallengeLink],
  );

  return (
    <CustomTableOverlay
      isOpen={isOpen}
      onClose={onClose}
      defaultVariant={defaultVariant}
      defaultTimeControl={defaultTimeControl}
      isGuest={isGuest}
      busy={busy}
      onCreate={allowPublicTable ? handleCreate : undefined}
      onPlayInPerson={onPlayInPerson ? handlePlayInPerson : undefined}
      onPlayAI={onPlayAI ? handlePlayAI : undefined}
      onSendChallenge={onSendChallenge ? handleSendChallenge : undefined}
      onCreateChallengeLink={onCreateChallengeLink ? handleCreateChallengeLink : undefined}
      onOpenOpeningTables={onOpenOpeningTables}
      onSignIn={onSignIn}
      onSpectate={onSpectate}
      preselectedUser={preselectedUser}
      defaultOpponent={defaultOpponent}
      position={position}
      lockRated={lockRated}
      rememberSettings={rememberSettings}
    />
  );
}
